import { useCallback, useMemo } from 'react';

import { isCurrencyInfo } from '@/utilities/isCurrencyInfo';

import type { CompanyTimeSeries } from '../../../../hooks/useCompanyTimeSeries';
import {
  getTableHeaders,
  useTableBody,
  formatCurrencyForCellData,
} from './utils';
import HorizontalDataTable from '@/components/HorizontalDataTable';
import { usePastTranslation } from '@/pages/CompanyReport/components/Past/hooks/usePastTranslation';

interface Props {
  rawData?: CompanyTimeSeries;
}

export const HistoricalFinanceTable = ({ rawData }: Props) => {
  const t = usePastTranslation();

  const getTableBody = useTableBody();

  const currencyInfo = rawData?.data.currencyInfo;

  const { heads, bodyData } = useMemo(
    () => ({
      heads: getTableHeaders(rawData),
      bodyData: getTableBody(rawData),
    }),
    [getTableBody, rawData]
  );

  const caption = useMemo(() => {
    if (!rawData) {
      return '';
    }
    const currency = isCurrencyInfo(currencyInfo)
      ? `(${currencyInfo.reporting_currency_iso} ${currencyInfo.reporting_unit_text})`
      : '';
    return t('revenue.table.caption', { name: rawData.name, currency });
  }, [rawData, t]);

  const customBodyCell = useCallback(
    ({ cellData }) =>
      formatCurrencyForCellData(
        cellData,
        isCurrencyInfo(currencyInfo)
          ? currencyInfo.reporting_currency_iso
          : 'USD'
      ),
    [currencyInfo]
  );

  return (
    <HorizontalDataTable
      headers={heads}
      bodyData={bodyData}
      caption={caption}
      customBodyCell={customBodyCell}
    />
  );
};
